import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, X, Radio, User, Menu, ChevronDown, ChevronRight, House, Megaphone, Handshake, Mail } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { categoriesAPI } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import LanguageSwitcher from '../common/LanguageSwitcher';

const BRAND_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo-makaya-optimized.png`;
const DEFAULT_CATEGORIES = [
  { slug: 'actualites', name: 'Actualités', subcategories: [] },
  { slug: 'politique', name: 'Politique', subcategories: [] },
  { slug: 'economie', name: 'Économie', subcategories: [] },
  { slug: 'societe', name: 'Société', subcategories: [] },
  { slug: 'culture', name: 'Culture', subcategories: [] },
  { slug: 'sport', name: 'Sport', subcategories: [] },
  { slug: 'sante', name: 'Santé', subcategories: [] },
  { slug: 'voyages-tourisme', name: 'Voyages & Tourisme', subcategories: [] },
  { slug: 'divertissement-vlog', name: 'Divertissement & Vlog', subcategories: [] },
  { slug: 'international', name: 'International', subcategories: [] },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { language, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [openCategorySlugs, setOpenCategorySlugs] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const activeCategorySlug = location.pathname.startsWith('/categorie/')
    ? decodeURIComponent(location.pathname.split('/')[2] || '')
    : '';

  const loadCategories = useCallback(() => {
    if (categoriesLoaded) return;
    categoriesAPI.list().then((r) => {
      setCategories(r.data?.length ? r.data : DEFAULT_CATEGORIES);
      setCategoriesLoaded(true);
    }).catch(() => {});
  }, [categoriesLoaded]);

  useEffect(() => {
    setCategoriesLoaded(false);
    setCategories(DEFAULT_CATEGORIES);
  }, [language]);

  useEffect(() => {
    if (desktopMenuOpen || mobileOpen) loadCategories();
  }, [desktopMenuOpen, loadCategories, mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setDesktopMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/recherche?q=${encodeURIComponent(searchQuery)}`);
    setSearchOpen(false);
    setSearchQuery('');
  };

  const handleMobileClose = () => setMobileOpen(false);
  const handleDesktopMenuClose = () => setDesktopMenuOpen(false);
  const toggleCategory = (slug) => {
    setOpenCategorySlugs((current) =>
      current.includes(slug)
        ? current.filter((item) => item !== slug)
        : [...current, slug],
    );
  };

  const sideLinks = [
    { to: '/', label: 'Accueil', icon: House },
    { to: '/radio', label: t('header.radio_live', 'Radio En Direct'), icon: Radio },
    { to: '/publicite', label: t('header.advertising', 'Publicité'), icon: Megaphone },
    { to: '/partenaires', label: t('header.partners', 'Partenaires'), icon: Handshake },
    { to: '/contact', label: t('header.contact', 'Contact'), icon: Mail },
  ];

  return (
    <>
      <div className="hidden lg:block bg-[#c4170c] text-white border-b border-[#a11008]" data-testid="top-header-bar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.08em] font-semibold opacity-95">
            {new Date().toLocaleDateString(
              'fr-FR',
              { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
            )}
          </div>
          <div className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.08em]">
            <Link to="/contact" className="hover:underline" data-testid="top-contact-link">{t('header.contact')}</Link>
            <Link to="/vous-reporter" className="hover:underline" data-testid="top-reporter-link">{t('header.reporter')}</Link>
            {user ? (
              <Link
                to={user.role === 'admin' || user.role === 'editor' ? '/admin' : '/profil'}
                className="hover:underline"
                data-testid="top-profile-link"
              >
                {t('header.my_account')}
              </Link>
            ) : (
              <Link to="/connexion" className="hover:underline" data-testid="top-login-link">{t('header.login')}</Link>
            )}
            <LanguageSwitcher variant="dark" />
          </div>
        </div>
      </div>

      <header className="bg-[#fffaf2] border-b border-[#e5d3d0] shadow-[0_10px_24px_rgba(66,28,17,0.08)] sticky top-0 z-50" data-testid="main-header">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="grid min-h-[6.25rem] grid-cols-[1fr_auto_1fr] items-center gap-3 py-2">
            <div className="flex items-center">
              <button
                onClick={() => setDesktopMenuOpen(true)}
                className="hidden lg:inline-flex items-center gap-2 rounded-full border border-[#ead8d1] bg-white/55 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-700 shadow-sm transition-colors hover:border-[#c4170c]/35 hover:text-[#c4170c]"
                aria-label={t('header.open_menu', 'Ouvrir le menu')}
                data-testid="desktop-side-menu-btn"
              >
                <Menu size={17} />
                {t('header.menu', 'Menu')}
              </button>
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden inline-flex items-center gap-2 text-gray-700 hover:text-[#c4170c]"
                aria-label={t('header.open_menu', 'Ouvrir le menu')}
                data-testid="mobile-menu-btn"
              >
                <Menu size={20} />
              </button>
            </div>

            <div className="flex justify-center">
              <Link to="/" className="flex items-center justify-center" data-testid="header-logo" aria-label="Makaya Enfo">
                <span className="flex h-[4.75rem] w-[15.5rem] items-center justify-center overflow-hidden">
                  <img
                    src={BRAND_LOGO_SRC}
                    alt="Makaya Enfo"
                    className="h-auto w-full object-contain"
                    loading="eager"
                    decoding="async"
                  />
                </span>
              </Link>
            </div>

            <div className="flex items-center justify-end gap-1.5 md:gap-3">
              <Link
                to="/radio"
                className="hidden md:flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-700 hover:text-[#c4170c] transition-colors"
                data-testid="header-radio-btn"
              >
                <span className="w-2 h-2 bg-[#c4170c] rounded-full pulse-red"></span>
                {t('header.live')}
              </Link>

              <button
                onClick={() => setSearchOpen((prev) => !prev)}
                className="p-2 text-gray-700 hover:text-[#c4170c] transition-colors"
                data-testid="header-search-btn"
                aria-label={t('header.search_aria', 'Rechercher')}
              >
                <Search size={18} />
              </button>

              {user && (
                <div className="hidden md:flex items-center gap-3">
                  <button
                    onClick={logout}
                    className="text-gray-600 hover:text-[#111827] text-[11px] uppercase tracking-[0.08em] font-semibold"
                    data-testid="header-logout-btn"
                  >
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="h-[2px] bg-gradient-to-r from-[#c4170c] via-[#a855f7] to-[#5b21b6]" />

        <div className="hidden lg:block border-t border-[#ebe7ff] bg-[#fffaf2]">
          <nav className="max-w-[1200px] mx-auto px-4 flex items-center gap-1 overflow-x-auto" data-testid="desktop-nav">
            {categories.slice(0, 10).map((cat) => (
              <Link
                key={cat.slug}
                to={`/categorie/${cat.slug}`}
                className={`px-2 py-3 text-[11px] font-bold transition-colors uppercase tracking-[0.08em] whitespace-nowrap ${
                  activeCategorySlug === cat.slug
                    ? 'text-[#c4170c]'
                    : 'text-gray-700 hover:text-[#c4170c]'
                }`}
                data-testid={`nav-cat-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </nav>
        </div>

        {searchOpen && (
          <div className="absolute inset-x-0 top-full z-[60] bg-white border-b border-gray-200 shadow-md p-4 fade-in-up">
            <form onSubmit={handleSearch} className="max-w-[800px] mx-auto flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('header.search_placeholder')}
                className="flex-1 bg-white border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#c4170c]"
                autoFocus
                data-testid="search-input"
              />
              <button
                type="submit"
                className="bg-[#c4170c] text-white px-6 py-3 font-bold uppercase text-sm tracking-wider hover:bg-[#a11008] transition-colors"
                data-testid="search-submit-btn"
              >
                {t('header.search_button')}
              </button>
            </form>
          </div>
        )}
      </header>

      {desktopMenuOpen && (
        <div className="hidden lg:block">
          <div className="fixed inset-0 z-[65] bg-slate-950/35" onClick={handleDesktopMenuClose} />
          <aside className="fixed inset-y-0 left-0 z-[70] w-[380px] max-w-[92vw] overflow-hidden border-r border-[#ead8d1] bg-[#fffaf2] shadow-[24px_0_70px_rgba(15,23,42,0.18)]" data-testid="desktop-side-menu">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-[#ead8d1] px-5 py-4">
                <span className="text-lg font-black tracking-tight text-[#c4170c]" style={{ fontFamily: 'var(--mk-heading-font)' }}>{t('header.menu', 'Menu')}</span>
                <button onClick={handleDesktopMenuClose} className="rounded-full p-2 text-gray-600 transition-colors hover:bg-[#f4e8df] hover:text-[#111827]" aria-label={t('header.close_menu', 'Fermer le menu')}>
                  <X size={18} />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-5 py-5">
                <div className="mb-6 grid grid-cols-1 gap-2">
                  {sideLinks.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={handleDesktopMenuClose}
                        className="flex min-h-[42px] items-center gap-3 rounded-[14px] border border-[#ead8d1] bg-white/70 px-3 text-sm font-semibold text-gray-800 transition-colors hover:border-[#c4170c]/30 hover:text-[#c4170c]"
                      >
                        <Icon size={16} className="text-[#c4170c]" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>

                <div className="space-y-2">
                  {categories.map((cat) => {
                    const subcategories = Array.isArray(cat.subcategories) ? cat.subcategories : [];
                    const isOpen = openCategorySlugs.includes(cat.slug);
                    const isActive = activeCategorySlug === cat.slug;
                    return (
                      <div key={cat.slug} className="border-b border-[#ead8d1]/75 pb-2">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/categorie/${cat.slug}`}
                            onClick={handleDesktopMenuClose}
                            className={`flex-1 py-2 text-sm font-bold uppercase tracking-[0.08em] transition-colors ${
                              isActive ? 'text-[#c4170c]' : 'text-gray-800 hover:text-[#c4170c]'
                            }`}
                          >
                            {cat.name}
                          </Link>
                          {subcategories.length > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleCategory(cat.slug)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-[#f4e8df] hover:text-[#c4170c]"
                              aria-label={cat.name}
                            >
                              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          )}
                        </div>
                        {isOpen && subcategories.length > 0 && (
                          <div className="space-y-1 pb-2 pl-4">
                            {subcategories.map((subcat) => (
                              <Link
                                key={subcat.slug}
                                to={`/categorie/${subcat.slug}`}
                                onClick={handleDesktopMenuClose}
                                className={`block rounded-[12px] px-3 py-2 text-sm transition-colors ${
                                  activeCategorySlug === subcat.slug
                                    ? 'bg-[#fff5f5] text-[#c4170c]'
                                    : 'text-gray-600 hover:bg-white/70 hover:text-[#c4170c]'
                                }`}
                              >
                                {subcat.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </nav>
            </div>
          </aside>
        </div>
      )}

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-[#fbfaff] pt-4" data-testid="mobile-menu-sheet">
          <div className="px-4 pb-4 border-b border-[#ddd6fe] flex items-center justify-between">
            <span className="text-xl font-black tracking-tight text-[#7c3aed]" style={{ fontFamily: 'var(--mk-heading-font)' }}>{t('header.menu', 'Menu')}</span>
            <button onClick={handleMobileClose} className="text-gray-600 hover:text-[#111827]" aria-label={t('header.close_menu', 'Fermer le menu')}>
              <X size={20} />
            </button>
          </div>

          <nav className="h-[calc(100dvh-74px)] overflow-y-auto p-5 flex flex-col gap-3" data-testid="mobile-nav">
            {categories.map((cat) => {
              const subcategories = Array.isArray(cat.subcategories) ? cat.subcategories : [];
              return (
                <div key={cat.slug} className="border-b border-[#e9ddff] pb-3">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/categorie/${cat.slug}`}
                      className={`flex-1 text-[15px] font-semibold uppercase tracking-[0.06em] ${
                        activeCategorySlug === cat.slug
                          ? 'text-[#c4170c]'
                          : 'text-gray-800 hover:text-[#c4170c]'
                      }`}
                      onClick={handleMobileClose}
                    >
                      {cat.name}
                    </Link>
                    {subcategories.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat.slug)}
                        className="h-8 w-8 inline-flex items-center justify-center text-gray-500"
                      >
                        {openCategorySlugs.includes(cat.slug) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    )}
                  </div>
                  {openCategorySlugs.includes(cat.slug) && subcategories.length > 0 && (
                    <div className="mt-3 flex flex-col gap-2 pl-4">
                      {subcategories.map((subcat) => (
                        <Link
                          key={subcat.slug}
                          to={`/categorie/${subcat.slug}`}
                          onClick={handleMobileClose}
                          className={`text-sm ${
                            activeCategorySlug === subcat.slug ? 'text-[#c4170c] font-semibold' : 'text-gray-600'
                          }`}
                        >
                          {subcat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="border-t border-gray-200 pt-4 mt-2 flex flex-col gap-3">
              <LanguageSwitcher variant="light" className="mb-2" />
              <Link to="/radio" onClick={handleMobileClose} className="flex items-center gap-2 text-gray-700 font-medium">
                <Radio size={16} className="text-[#c4170c]" /> {t('header.radio_live')}
              </Link>
              {user ? (
                <button
                  onClick={() => {
                    logout();
                    handleMobileClose();
                  }}
                  className="text-gray-600 w-fit"
                >
                  {t('header.logout')}
                </button>
              ) : (
                <Link to="/connexion" onClick={handleMobileClose} className="text-gray-700">
                  <User size={16} className="inline mr-1" /> {t('header.login')}
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
